import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  UserIcon,
  HomeIcon,
  Bars3Icon,
  BellIcon
} from '@heroicons/react/24/outline';
import { AuthContext } from '../contexts/AuthContext';
import InviteNotifications from './InviteNotifications';
import useClickOutside from '../hooks/useClickOutside';

interface AppHeaderProps {
  hasUnsavedChanges?: boolean;
  onNavigateAway?: (destination: string) => Promise<boolean>; // Returns true if navigation should proceed
  showSaveIndicator?: boolean;
  showAuthSection?: boolean; // Hide profile/notification UI on landing page
}

const AppHeader: React.FC<AppHeaderProps> = ({ 
  hasUnsavedChanges = false, 
  onNavigateAway,
  showSaveIndicator = false,
  showAuthSection = true 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const authContext = useContext(AuthContext);
  const currentUser = authContext?.user;
  
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const profileDropdownRef = useRef<HTMLDivElement>(null);

  // Close profile dropdown when clicking outside
  useClickOutside(profileDropdownRef, () => setShowProfileDropdown(false));

  // Close dropdowns on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowProfileDropdown(false);
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);



  // Handle navigation with unsaved changes check
  const handleNavigation = async (destination: string) => {
    if (hasUnsavedChanges && onNavigateAway) {
      const canNavigate = await onNavigateAway(destination);
      if (canNavigate) {
        navigate(destination);
      }
    } else {
      navigate(destination);
    }
  };



  return (
    <>
      {/* Header */}
      <header className="flex items-center justify-between px-4 sm:px-6 lg:px-8 py-4 bg-white/80 backdrop-blur-sm shadow-sm border-b border-gray-200 relative z-[9999]">
        {/* Logo */}
        <div 
          className="text-xl sm:text-2xl lg:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 cursor-pointer"
          onClick={() => handleNavigation('/dashboard')}
        >
          StyleSync
          {showSaveIndicator && (
            <span className="ml-2 text-xs text-orange-500">(Unsaved)</span>
          )}
        </div>
        
        {/* Desktop Icons - show only when allowed and user is logged in */}
        {showAuthSection && currentUser && (
        <div className="hidden sm:flex items-center space-x-4 lg:space-x-6 text-gray-500 relative">
          <InviteNotifications />
          
          <div className="relative" ref={profileDropdownRef}>
            <UserIcon 
              className="w-5 h-5 lg:w-6 lg:h-6 cursor-pointer hover:text-blue-500 transition-colors"
              onClick={() => setShowProfileDropdown(v=>!v)}
            />
            {showProfileDropdown && (
              <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] transform transition-all duration-200 ease-in-out">
                <div className="p-3 border-b border-gray-100 bg-gray-50 rounded-t-lg">
                  <div className="text-sm font-medium text-gray-900">{currentUser?.displayName || 'User'}</div>
                  <div className="text-xs text-gray-500 truncate">{currentUser?.email}</div>
                </div>
                <div className="py-1">
                  <button
                    className="w-full text-left px-4 py-3 hover:bg-gray-100 text-sm font-medium text-gray-700 transition-colors focus:outline-none focus:bg-gray-100"
                    onClick={() => { 
                      setShowProfileDropdown(false); 
                      handleNavigation('/profile'); 
                    }}
                  >
                    Profile
                  </button>
                  <button
                    className="w-full text-left px-4 py-3 hover:bg-gray-100 text-sm font-medium text-red-600 transition-colors focus:outline-none focus:bg-gray-100"
                    onClick={async () => { 
                      setShowProfileDropdown(false); 
                      await authContext?.logout(); 
                    }}
                  >
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>)}
        
        {/* Mobile Menu Button - show only when allowed and user is logged in */}
        {showAuthSection && currentUser && (
        <div className="sm:hidden flex items-center space-x-3">
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <Bars3Icon className="w-6 h-6 text-gray-600" />
          </button>
        </div>)}
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="sm:hidden fixed inset-0 z-[1000] bg-black bg-opacity-50" onClick={() => setIsMobileMenuOpen(false)}>
          <div className="absolute right-0 top-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl p-6 transform transition-transform duration-300 ease-in-out" onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-xl font-semibold text-gray-900">Menu</h3>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  aria-label="Close menu"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* User Info */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                    <UserIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{currentUser?.displayName || 'User'}</div>
                    <div className="text-sm text-gray-500 truncate">{currentUser?.email}</div>
                  </div>
                </div>
              </div>

              {/* Navigation */}
              <div className="flex-1 flex flex-col space-y-2">
                <button
                  onClick={() => {
                    handleNavigation('/dashboard');
                    setIsMobileMenuOpen(false);
                  }}
                  className="flex items-center space-x-3 p-4 rounded-lg hover:bg-gray-100 transition-colors text-left"
                >
                  <HomeIcon className="w-5 h-5 text-gray-600" />
                  <span className="text-gray-700 font-medium">Dashboard</span>
                </button>
                <button
                  onClick={() => {
                    handleNavigation('/profile');
                    setIsMobileMenuOpen(false);
                  }}
                  className="flex items-center space-x-3 p-4 rounded-lg hover:bg-gray-100 transition-colors text-left"
                >
                  <UserIcon className="w-5 h-5 text-gray-600" />
                  <span className="text-gray-700 font-medium">Profile</span>
                </button>
                
                {/* Notifications Section */}
                <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                  <div className="flex items-center space-x-3 mb-3">
                    <BellIcon className="w-5 h-5 text-blue-600" />
                    <span className="text-blue-800 font-medium">Notifications</span>
                  </div>
                  <InviteNotifications />
                </div>
              </div>

              {/* Logout Button */}
              <div className="mt-auto pt-4 border-t border-gray-200">
                <button
                  onClick={async () => {
                    setIsMobileMenuOpen(false);
                    await authContext?.logout();
                  }}
                  className="w-full flex items-center justify-center space-x-2 p-4 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors font-medium"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


    </>
  );
};

export default AppHeader; 